package songwritersnotebook.app.data

import kotlinx.datetime.Clock
import kotlinx.datetime.Instant
import kotlinx.serialization.Serializable
import kotlinx.serialization.Transient
import com.benasher44.uuid.uuid4
import songwritersnotebook.app.utils.ChordUtils

import kotlinx.serialization.KSerializer
import kotlinx.serialization.SerializationException
import kotlinx.serialization.descriptors.PrimitiveKind
import kotlinx.serialization.descriptors.PrimitiveSerialDescriptor
import kotlinx.serialization.builtins.MapSerializer
import kotlinx.serialization.builtins.serializer
import kotlinx.serialization.descriptors.SerialDescriptor
import kotlinx.serialization.descriptors.buildClassSerialDescriptor
import kotlinx.serialization.descriptors.element
import kotlinx.serialization.encoding.CompositeDecoder
import kotlinx.serialization.encoding.Decoder
import kotlinx.serialization.encoding.Encoder
import kotlinx.serialization.json.JsonDecoder
import kotlinx.serialization.json.JsonEncoder
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonPrimitive
import kotlinx.serialization.json.double
import kotlinx.serialization.json.float
import kotlinx.serialization.json.long
import kotlinx.serialization.json.longOrNull
import kotlinx.serialization.json.int
import kotlinx.serialization.json.jsonPrimitive
import kotlinx.serialization.json.intOrNull
import kotlinx.serialization.json.doubleOrNull
import kotlinx.serialization.json.floatOrNull

object FlexibleIntSerializer : KSerializer<Int> {
    override val descriptor: SerialDescriptor = PrimitiveSerialDescriptor("FlexibleInt", PrimitiveKind.INT)

    override fun serialize(encoder: Encoder, value: Int) {
        encoder.encodeInt(value)
    }

    override fun deserialize(decoder: Decoder): Int {
        if (decoder is JsonDecoder) {
            val element = try { decoder.decodeJsonElement() } catch (e: Exception) { return 0 }
            return if (element is JsonPrimitive) {
                if (element.isString) {
                    element.content.toIntOrNull() ?: element.content.toDoubleOrNull()?.toInt() ?: 0
                } else {
                    element.intOrNull ?: element.doubleOrNull?.toInt() ?: 0
                }
            } else {
                0
            }
        }
        // Fallback for other decoders
        return try {
            decoder.decodeInt()
        } catch (e: Exception) {
            try {
                decoder.decodeLong().toInt()
            } catch (e2: Exception) {
                 try {
                    decoder.decodeDouble().toInt()
                 } catch (e3: Exception) {
                    0
                 }
            }
        }
    }
}

object FlexibleFloatSerializer : KSerializer<Float> {
    override val descriptor: SerialDescriptor = PrimitiveSerialDescriptor("FlexibleFloat", PrimitiveKind.FLOAT)

    override fun serialize(encoder: Encoder, value: Float) {
        if (value.isNaN() || value.isInfinite()) {
            encoder.encodeFloat(0f)
        } else {
            encoder.encodeFloat(value)
        }
    }

    override fun deserialize(decoder: Decoder): Float {
        if (decoder is JsonDecoder) {
            val element = try { decoder.decodeJsonElement() } catch (e: Exception) { return 0f }
            return if (element is JsonPrimitive) {
                if (element.isString) {
                    val f = element.content.toFloatOrNull() ?: 0f
                    if (f.isNaN() || f.isInfinite()) 0f else f
                } else {
                    val f = element.floatOrNull ?: 0f
                    if (f.isNaN() || f.isInfinite()) 0f else f
                }
            } else {
                0f
            }
        }
        // Fallback for other decoders (like Firestore)
        return try {
            val d = decoder.decodeDouble()
            if (d.isNaN() || d.isInfinite()) 0f else d.toFloat()
        } catch (e: Exception) {
            try {
                val f = decoder.decodeFloat()
                if (f.isNaN() || f.isInfinite()) 0f else f
            } catch (e2: Exception) {
                0f
            }
        }
    }
}


object FirestoreLongSerializer : KSerializer<Long> {
    override val descriptor: SerialDescriptor = PrimitiveSerialDescriptor("FirestoreLong", PrimitiveKind.DOUBLE)

    override fun serialize(encoder: Encoder, value: Long) {
        // We serialize as Double so that dev.gitlive.firebase maps it to a plain JS Number natively
        encoder.encodeDouble(value.toDouble())
    }

    override fun deserialize(decoder: Decoder): Long {
        if (decoder is JsonDecoder) {
            val element = try { decoder.decodeJsonElement() } catch (e: Exception) { return 0L }
            return if (element is JsonPrimitive) {
                if (element.isString) {
                    element.content.toLongOrNull() ?: element.content.toDoubleOrNull()?.toLong() ?: 0L
                } else {
                    element.longOrNull ?: element.doubleOrNull?.toLong() ?: 0L
                }
            } else if (element is JsonObject) {
                // In case it's literally saved as { low: ..., high: ... }
                val low = element["low"]?.jsonPrimitive?.longOrNull ?: element["low_"]?.jsonPrimitive?.longOrNull ?: 0L
                val high = element["high"]?.jsonPrimitive?.longOrNull ?: element["high_"]?.jsonPrimitive?.longOrNull ?: 0L
                if (low != 0L) low else 0L
            } else {
                0L
            }
        }
        
        // Fallback for generic decoders
        return try {
            decoder.decodeLong()
        } catch (e: Exception) {
             try {
                decoder.decodeDouble().toLong()
             } catch (e2: Exception) {
                  try {
                       decoder.decodeString().toLong()
                  } catch (e3: Exception) {
                       0L
                  }
             }
        }
    }
}

object FirestoreInstantSerializer : KSerializer<Instant> {
    override val descriptor: SerialDescriptor = buildClassSerialDescriptor("FirestoreInstant") {
        element<Double>("seconds")
        element<Int>("nanoseconds")
    }

    override fun serialize(encoder: Encoder, value: Instant) {
        // Safe check for JsonEncoder to avoid IllegalStateException in some environments
        val jsonEncoder = try {
            if (encoder is JsonEncoder) encoder else null
        } catch (e: Exception) {
            null
        }

        if (jsonEncoder != null) {
            encoder.encodeLong(value.toEpochMilliseconds())
        } else {
            // For Firestore and other structured formats
            try {
                val composite = encoder.beginStructure(descriptor)
                composite.encodeDoubleElement(descriptor, 0, value.epochSeconds.toDouble())
                composite.encodeIntElement(descriptor, 1, value.nanosecondsOfSecond)
                composite.endStructure(descriptor)
            } catch (e: Exception) {
                // Last resort fallback for primitive-only encoders
                encoder.encodeLong(value.toEpochMilliseconds())
            }
        }
    }

    override fun deserialize(decoder: Decoder): Instant {
        val jsonDecoder = try {
            if (decoder is JsonDecoder) decoder else null
        } catch (e: Exception) {
            null
        }

        if (jsonDecoder != null) {
            return try {
                val element = jsonDecoder.decodeJsonElement()
                if (element is JsonObject) {
                    val seconds = element["seconds"]?.jsonPrimitive?.long ?: 0L
                    val nanos = element["nanoseconds"]?.jsonPrimitive?.int ?: 0
                    Instant.fromEpochSeconds(seconds, nanos)
                } else if (element is JsonPrimitive) {
                     if (element.isString) {
                         try {
                            Instant.parse(element.content)
                         } catch (e: Exception) {
                             // Try parsing string as Long
                             try {
                                 Instant.fromEpochMilliseconds(element.content.toLong())
                             } catch (e2: Exception) {
                                 println("FirestoreInstantSerializer: Failed to parse string instant '${element.content}': $e")
                                 Clock.System.now()
                             }
                         }
                     } else {
                         // Number (Long or Double)
                         try {
                             Instant.fromEpochMilliseconds(element.long)
                         } catch (e: Exception) {
                             try {
                                 Instant.fromEpochMilliseconds(element.double.toLong())
                             } catch (e2: Exception) {
                                 println("FirestoreInstantSerializer: Failed to parse number instant: $e")
                                 Clock.System.now()
                             }
                         }
                     }
                } else {
                    println("FirestoreInstantSerializer: Unknown element type $element")
                    Clock.System.now()
                }
            } catch (e: Exception) {
                 // Fallback
                 try {
                    Instant.fromEpochMilliseconds(decoder.decodeLong())
                 } catch (e2: Exception) {
                    println("FirestoreInstantSerializer (Json): Fallback failed: $e. Origin: $e2")
                    Clock.System.now()
                 }
            }
        } else {
            // Generic decoder (e.g. Firebase)
            // Try structure first (for Timestamp objects)
            try {
                val composite = decoder.beginStructure(descriptor)
                var seconds = 0L
                var nanos = 0
                loop@ while (true) {
                    when (val index = composite.decodeElementIndex(descriptor)) {
                        CompositeDecoder.DECODE_DONE -> break@loop
                        0 -> seconds = try { composite.decodeLongElement(descriptor, 0) } catch(e: Exception) { composite.decodeDoubleElement(descriptor, 0).toLong() }
                        1 -> nanos = composite.decodeIntElement(descriptor, 1)
                        else -> { /* Skip */ }
                    }
                }
                composite.endStructure(descriptor)
                return Instant.fromEpochSeconds(seconds, nanos)
            } catch (e: Exception) {
                // If structure fails, it might be a primitive (Long/Double/String) stored in Firestore
                // Note: Generic decoders might have consumed state. 
                // However, for many (like Firebase), if beginStructure matches no map, it might throw immediately or allow fallback?
                // Actually, if we are here, we are assuming 'beginStructure' failed or decoding elements failed.
                
                // Try decoding as simple primitives.
                // We use a safe approach by trying common types.
                try {
                     return Instant.fromEpochMilliseconds(decoder.decodeLong())
                } catch (e2: Exception) {
                    try {
                        // Maybe it's a Double?
                         return Instant.fromEpochMilliseconds(decoder.decodeDouble().toLong())
                    } catch (e3: Exception) {
                        try {
                            // Maybe it's a String?
                            val s = decoder.decodeString()
                            return try {
                                Instant.parse(s)
                            } catch (e4: Exception) {
                                try {
                                     // Try parsing "Timestamp(seconds=..., nanoseconds=...)" format (common from Firestore SDK toString)
                                     val match = Regex("Timestamp\\(seconds=(-?\\d+), nanoseconds=(-?\\d+)\\)").find(s)
                                     if (match != null) {
                                         val (sec, nano) = match.destructured
                                         Instant.fromEpochSeconds(sec.toLong(), nano.toInt())
                                     } else {
                                         Instant.fromEpochMilliseconds(s.toLong())
                                     }
                                } catch (e5: Exception) {
                                    throw e4 // Throw original parse exception if regex/long fail
                                }
                            }
                        } catch (e4: Exception) {
                            println("FirestoreInstantSerializer (Generic): All attempts failed. Error: $e. Fallback errors: $e2, $e3, $e4")
                            return Clock.System.now()
                        }
                    }
                }
            }
        }
    }
}

@Serializable
data class Line(
    val id: String = uuid4().toString(),
    val text: String = "",
    val tabs: List<GuitarTab> = emptyList(),
    val position: String = "m",
    val sectionId: String = "",
    val wordCount: Int = 0,
    @Serializable(with = FirestoreLongSerializer::class)
    val lastModified: Long = Clock.System.now().toEpochMilliseconds()
) {
    companion object {
        fun createEmpty(sectionId: String = "", position: String = "m"): Line {
            return Line(text = "", tabs = emptyList(), sectionId = sectionId, position = position)
        }
    }
}

@Serializable
data class Section(
    val id: String = uuid4().toString(),
    val title: String = "",
    val lines: List<Line> = emptyList(),
    val tabs: List<GuitarTab> = emptyList(),
    val position: String = "m",
    @Serializable(with = FirestoreLongSerializer::class)
    val lastModified: Long = Clock.System.now().toEpochMilliseconds()
)

@Serializable
data class ChordDefinition(
    val name: String = "",
    val root: String? = null,
    val quality: String? = null,
    val pianoNotes: List<String>? = null,
    val guitarTab: List<String>? = null,
    val isCustom: Boolean = false,
    @Serializable(with = FirestoreLongSerializer::class)
    val lastModified: Long = Clock.System.now().toEpochMilliseconds()
)

object SafeChordMapSerializer : KSerializer<Map<String, ChordDefinition>> {
    private val delegate by lazy { MapSerializer(String.serializer(), ChordDefinition.serializer()) }
    override val descriptor: kotlinx.serialization.descriptors.SerialDescriptor = delegate.descriptor

    override fun serialize(encoder: Encoder, value: Map<String, ChordDefinition>) {
        delegate.serialize(encoder, value)
    }

    override fun deserialize(decoder: Decoder): Map<String, ChordDefinition> {
        return try {
            delegate.deserialize(decoder)
        } catch (e: Exception) {
            println("DEBUG_SYNC: SafeChordMapSerializer caught exception: $e")
            emptyMap()
        }
    }
}

@Serializable
data class ChordProgression(
    val id: String = uuid4().toString(),
    val name: String = "",
    val chordNames: List<String> = emptyList(),
    val intervals: List<String> = emptyList(),
    val isFavorite: Boolean = false,
    val keyRoot: String? = null,
    val isMajor: Boolean? = null,
    @Serializable(with = FirestoreLongSerializer::class)
    val lastModified: Long = Clock.System.now().toEpochMilliseconds()
)

@Serializable
data class RecordingMetadata(
    val id: String = uuid4().toString(),
    val filePath: String = "",
    val label: String = "Recording",
    @Serializable(with = FirestoreLongSerializer::class)
    val timestamp: Long = Clock.System.now().toEpochMilliseconds(),
    @Serializable(with = FirestoreLongSerializer::class)
    val durationMs: Long = 0L,
    @Transient val isSynced: Boolean = true
)

@Serializable
data class TrackData(
    val text: String? = null,
    val originalLineText: String? = null,
    val section: String? = null,
    @Serializable(with = FlexibleIntSerializer::class)
    val line: Int? = null,
    val name: String? = null,
    val instrument: String? = null,
    val notes: List<String>? = null,
    val id: String? = null,
    val url: String? = null,
    val dummy: Boolean? = null
)

@Serializable
data class TrackItem(
    val id: String = uuid4().toString(),
    @Serializable(with = FlexibleFloatSerializer::class)
    val beat: Float = 0f,
    @Serializable(with = FlexibleFloatSerializer::class)
    val duration: Float = 0f,
    val type: String = "",
    val data: TrackData = TrackData(),
    @Serializable(with = FlexibleFloatSerializer::class)
    val originalDurationBeats: Float? = null,
    @Serializable(with = FlexibleFloatSerializer::class)
    val trimStartBeats: Float = 0f,
    val position: String = "m",
    val trackId: String = "",
    @Serializable(with = FirestoreLongSerializer::class)
    val lastModified: Long = Clock.System.now().toEpochMilliseconds()
)

@Serializable
data class Track(
    val id: String = uuid4().toString(),
    val name: String = "",
    val items: List<TrackItem> = emptyList(),
    @Transient val isMuted: Boolean = false,
    @Transient val isSoloed: Boolean = false,
    val isCollapsed: Boolean = false,
    @Serializable(with = FlexibleFloatSerializer::class)
    val volume: Float = 1.0f,
    @Serializable(with = FlexibleFloatSerializer::class)
    val strumSpeed: Float = 1.0f,
    val position: String = "m",
    @Serializable(with = FirestoreLongSerializer::class)
    val lastModified: Long = Clock.System.now().toEpochMilliseconds()
)

@Serializable
data class TimeSignature(
    @Serializable(with = FlexibleIntSerializer::class)
    val beats: Int = 4,
    @Serializable(with = FlexibleIntSerializer::class)
    val per: Int = 4
)

@Serializable
data class SnapInterval(
    val base: String = "beat",
    @Serializable(with = FlexibleIntSerializer::class)
    val numerator: Int = 1,
    @Serializable(with = FlexibleIntSerializer::class)
    val denominator: Int = 4
)

@Serializable
enum class SnapMode {
    BEAT, BAR
}

@Serializable
data class StudioState(
    val tracks: List<Track> = emptyList(),
    val timeSignature: TimeSignature = TimeSignature(4, 4),
    @Transient val isMetronomeEnabled: Boolean = false,
    @Transient val isSnapEnabled: Boolean = true,
    @Transient val snapInterval: SnapInterval = SnapInterval(base = "beat", numerator = 1, denominator = 4),
    @Serializable(with = FlexibleFloatSerializer::class)
    @Transient val zoomX: Float = 1.0f,
    @Serializable(with = FlexibleFloatSerializer::class)
    @Transient val zoomY: Float = 1.0f
)

@Serializable
data class HarmonyChatMessage(
    val text: String,
    val isUser: Boolean,
    @Serializable(with = FirestoreLongSerializer::class)
    val timestamp: Long = Clock.System.now().toEpochMilliseconds(),
    val id: String = com.benasher44.uuid.uuid4().toString(),
    val userId: String? = null
)
@Serializable
data class UserPresence(
    val userId: String = "",
    val email: String = "",
    val deviceId: String = "",
    val username: String = "",
    val color: Int = 0,
    val cursorPosition: Int = 0,
    val activeSectionId: String? = null,
    val activeLineId: String? = null,
    val activeTrackId: String? = null,
    val activeTrackItemId: String? = null,
    val selectedTrackItemIds: Set<String> = emptySet(),
    val activeTabId: String? = null,
    val tabCursorColumn: Int = 0,
    val tabCursorString: Int = 0,
    val lastActive: Double = Clock.System.now().toEpochMilliseconds().toDouble()
) {
    companion object {
        fun generateUserColor(userId: String): Int {
            // Hash the string to a stable color
            val hash = userId.fold(0) { acc, char -> (acc * 31 + char.code) }
            val hue = (hash.coerceAtLeast(0) % 360).toFloat()
            // Neutral: Saturation 60-70%, Lightness 45-55%
            val s = 0.65f
            val l = 0.50f
            
            val c = (1f - kotlin.math.abs(2f * l - 1f)) * s
            val x = c * (1f - kotlin.math.abs((hue / 60f) % 2f - 1f))
            val m = l - c / 2f
            
            var (r, g, b) = when {
                hue < 60 -> Triple(c, x, 0f)
                hue < 120 -> Triple(x, c, 0f)
                hue < 180 -> Triple(0f, c, x)
                hue < 240 -> Triple(0f, x, c)
                hue < 300 -> Triple(x, 0f, c)
                else -> Triple(c, 0f, x)
            }
            
            val ri = ((r + m) * 255).toInt().coerceIn(0, 255)
            val gi = ((g + m) * 255).toInt().coerceIn(0, 255)
            val bi = ((b + m) * 255).toInt().coerceIn(0, 255)
            
            return (0xFF shl 24) or (ri shl 16) or (gi shl 8) or bi
        }
    }
}

@Serializable
data class Song(
    val id: String = uuid4().toString(),
    @Serializable(with = FirestoreLongSerializer::class)
    val sizeInBytes: Long = 0L,
    val title: String = "",
    val crdtState: String? = null, // Base64 encoded Automerge snapshot
    val crdtEpoch: Int = 0, // Increments when the cloud squashes the CRDT doc
    @Serializable(with = FirestoreInstantSerializer::class)
    val lastModified: Instant = Clock.System.now(),
    @Serializable(with = FirestoreInstantSerializer::class)
    val lastSynced: Instant? = null,
    val revisionId: String? = null,
    val lastActorId: String? = null, // The Automerge Actor ID that made the last change
    val heads: List<String> = emptyList(), // The Automerge "Heads" hashes for causal tracking
    @Transient
    val isSyncError: Boolean = false,
    val sections: List<Section> = listOf(Section(title = "Verse 1", lines = listOf(Line.createEmpty()))),
    val recordings: Map<String, List<RecordingMetadata>> = emptyMap(),
    val guitarTuning: List<String> = listOf("E2", "A2", "D3", "G3", "B3", "E4"),
    val guitarCapo: Int = 0,
    @Serializable(with = SafeChordMapSerializer::class)
    val chordDefinitions: Map<String, ChordDefinition> = emptyMap(),
    val progressions: List<ChordProgression> = emptyList(),
    val bpm: Int = 120,
    val studioState: StudioState? = null,
    val showPianoInfo: Boolean = false,
    val showGuitarInfo: Boolean = false,
    val notes: String = "",
    val showNotes: Boolean = false,
    // Sharing & Collaboration Fields
    val ownerId: String = "",
    val ownerUsername: String = "",
    val collaborators: List<String> = emptyList(), // UIDs with Edit access
    val viewers: List<String> = emptyList(), // UIDs with View access
    val collaboratorEmails: Map<String, String> = emptyMap(), // UID -> Email mapping for display
    val pendingCollaborators: Map<String, String> = emptyMap(), // Email -> Role ("editor" or "viewer")
    val isPublic: Boolean = false, // If true, anyone can view
    val isDeleted: Boolean = false, // Tombstone for sync
    val lockedSections: Map<String, String> = emptyMap(), // sectionId -> userId
    val lockedSectionTimestamps: Map<String, Long> = emptyMap(), // sectionId -> timestamp
    val lockedTracks: Map<String, String> = emptyMap(), // trackId -> userId
    val lockedTrackTimestamps: Map<String, Long> = emptyMap(), // trackId -> timestamp
    val hasActiveLocks: Boolean = false,
    @Transient
    @Deprecated("Moved to subcollection to prevent 1MB limit bloat")
    val harmonyChatHistory: List<HarmonyChatMessage> = emptyList(),
    @Transient
    val presence: Map<String, UserPresence> = emptyMap(),
    @Transient
    val isDirty: Boolean = false // Local flag for unsynced changes
) {
    /**
     * Ensures the song structure is clean before saving to permanent storage.
     * Removes duplicate IDs and ensures no orphaned data exists.
     */
    fun sanitize(): Song {
        val seenSectionIds = mutableSetOf<String>()
        val sanitizedSections = sections.map { section ->
            // Ensure ID exists
            val sId = if (section.id.isEmpty()) com.benasher44.uuid.uuid4().toString() else section.id
            
            val seenLineIds = mutableSetOf<String>()
            val sanitizedLines = section.lines.map { line ->
                // Ensure ID exists
                val lId = if (line.id.isEmpty()) com.benasher44.uuid.uuid4().toString() else line.id
                line.copy(id = lId, sectionId = sId)
            }.filter { line ->
                val isNew = line.id !in seenLineIds
                if (isNew) seenLineIds.add(line.id)
                isNew
            }
            section.copy(id = sId, lines = sanitizedLines)
        }.filter { section ->
            val isNew = section.id !in seenSectionIds
            if (isNew) seenSectionIds.add(section.id)
            isNew
        }

        // Only keep recordings for sections/lines that actually exist
        val validIds = seenSectionIds + sanitizedSections.flatMap { it.lines.map { l -> l.id } }
        val sanitizedRecordings = recordings.filter { it.key in validIds || it.key == id }

        return this.copy(
            sections = sanitizedSections,
            recordings = sanitizedRecordings
        )
    }

    companion object {
        private fun createDefaultChordDefinitions(): Map<String, ChordDefinition> {
            val defaultChordNames = listOf("C", "G", "Am", "F", "Dm", "E7")
            return defaultChordNames.associateWith { chordName ->
                val (root, qualityKey) = ChordUtils.parseChordName(chordName)
                val notes = ChordUtils.PIANO_CHORD_VOICINGS[chordName]
                    ?: ChordUtils.calculateChordNotes(root, qualityKey)
                val tab = ChordUtils.GUITAR_CHORD_TABS[chordName]?.map { it.toString() }

                ChordDefinition(
                    name = chordName,
                    root = root,
                    quality = qualityKey,
                    pianoNotes = notes,
                    isCustom = false,
                    guitarTab = tab
                )
            }
        }

        const val SAMPLE_WANDERING_SOUL_ID = "sample-wandering-soul"

        fun createSample(userId: String? = null): Song {
            val songId = if (userId.isNullOrBlank() || userId == "anonymous") {
                SAMPLE_WANDERING_SOUL_ID
            } else {
                "$SAMPLE_WANDERING_SOUL_ID-$userId"
            }
            val section1Id = uuid4().toString()
            val section2Id = uuid4().toString()

            return Song(
                id = songId,
                title = "Wandering Soul",
                ownerId = userId ?: "",
                sections = listOf(
                    Section(
                        id = section1Id,
                        title = "Verse 1",
                        position = "m",
                        lines = listOf(
                            Line(
                                text = "The [C]road goes on and [G]on,",
                                sectionId = section1Id,
                                position = "m"
                            ),
                            Line(text = "Beneath the [Am]pale blue [F]sky.", sectionId = section1Id, position = "p"),
                            Line(text = "I'm just a [C]wandering [G]soul,", sectionId = section1Id, position = "r"),
                            Line(text = "Who never [F]questioned [C]why.", sectionId = section1Id, position = "t")
                        )
                    ),
                    Section(
                        id = section2Id,
                        title = "Chorus",
                        position = "p",
                        lines = listOf(
                            Line(
                                text = "Oh, [F]sing me a [C]song of the [G]open [Am]road,",
                                sectionId = section2Id,
                                position = "m"
                            ),
                            Line(text = "A [F]melody [C]wild and [G]free.", sectionId = section2Id, position = "p"),
                            Line(text = "Carry my [F]burdens, [C]lighten my [G]load.", sectionId = section2Id, position = "r"),
                            Line(text = "And [F]bring my [G]spirit back to [C]me.", sectionId = section2Id, position = "t")
                        )
                    )
                ),
                chordDefinitions = createDefaultChordDefinitions()
            )
        }

        fun createEmpty(): Song {
            val sectionId = uuid4().toString()
            return Song(
                title = "Untitled Song",
                sections = listOf(Section(id = sectionId, title = "Verse 1", lines = listOf(Line.createEmpty(sectionId = sectionId)))),
                chordDefinitions = emptyMap(),
                isDeleted = false
            )
        }
    }

    fun withUpdatedSection(index: Int, section: Section): Song {
        val updatedSections = sections.toMutableList()
        updatedSections[index] = section
        return copy(sections = updatedSections)
    }
    
    fun withUpdatedLineInSection(sectionIndex: Int, newLine: Line): Song {
        val updatedSections = sections.mapIndexed { idx, section ->
            if (idx == sectionIndex) {
                section.copy(lines = section.lines + newLine)
            } else {
                section
            }
        }
        return copy(sections = updatedSections)
    }
    
    fun addSection(): Song {
        val newSection = Section(
            title = "Section ${sections.size + 1}",
            lines = listOf(Line.createEmpty()),
            position = songwritersnotebook.app.utils.FractionalIndexer.generateBetween(sections.lastOrNull()?.position, null)
        )
        return copy(sections = sections + newSection)
    }
}

